import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Upload, CheckCircle2, Eye, X, AlertTriangle } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { ProposalSectionBlock } from "@/components/shared/ProposalSectionBlock";

const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400";
const inputClass = "mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white";
const btn = "inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50";
const outlineBtn = "inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition-colors duration-150 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10";
const card = "rounded-2xl border border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-[#0F172A]";

const SOURCE_LABELS = { nova_standard: "Nova Standard", manual_custom: "Manual Custom", org_uploaded: "Existing Template" };
const STATUS_LABELS = { draft: "Draft", active: "Active", archived: "Archived" };
const CONFIDENCE_STYLES = {
  low: "border-red-300 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10",
  medium: "border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10",
};

const TemplatePreviewModal = ({ templateId, onClose, API }) => {
  const [sections, setSections] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/org/finance/templates/${templateId}/preview`, { withCredentials: true })
      .then(({ data }) => setSections(data.sections))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, templateId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="template-preview-modal">
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-slate-50 p-5">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-extrabold text-slate-900">Preview as Customer <span className="text-xs font-semibold text-slate-400">(sample data)</span></p>
          <button type="button" onClick={onClose} data-testid="template-preview-close-btn" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200"><X size={16} /></button>
        </div>
        {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-600" data-testid="template-preview-error">{error}</p>}
        {!error && !sections && <p className="text-sm text-slate-400">Loading…</p>}
        {sections && (
          <div className="space-y-3">
            {sections.map((s, i) => <ProposalSectionBlock key={i} section={s} />)}
          </div>
        )}
      </div>
    </div>
  );
};

const TemplateSectionsEditor = ({ template, sectionTypes, sectionLabels, onSave, onApprove, busy }) => {
  const [sections, setSections] = useState(template.sections || []);
  useEffect(() => { setSections(template.sections || []); }, [template.template_id, template.sections]);

  const update = (idx, field, value) => setSections((s) => s.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  const remove = (idx) => setSections((s) => s.filter((_, i) => i !== idx));
  const add = () => setSections((s) => [...s, { section_key: `custom_${Date.now()}`, type: "custom_text", label: "New Section", enabled: true }]);
  const lowConfidenceCount = sections.filter((s) => s.confidence === "low").length;

  return (
    <div className="mt-3 space-y-2" data-testid={`template-sections-editor-${template.template_id}`}>
      {lowConfidenceCount > 0 && (
        <p className="flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs font-bold text-red-600 dark:bg-red-500/10 dark:text-red-400" data-testid={`template-low-confidence-notice-${template.template_id}`}>
          <AlertTriangle size={13} /> {lowConfidenceCount} section{lowConfidenceCount > 1 ? "s" : ""} from Nova AI need careful review (highlighted below)
        </p>
      )}
      {sections.map((s, idx) => (
        <div key={s.section_key || idx} className={`rounded-lg border p-2 ${CONFIDENCE_STYLES[s.confidence] || "border-slate-200 dark:border-white/10"}`} data-testid={`template-section-row-${idx}`}>
          <div className="grid grid-cols-12 items-center gap-2">
            <select value={s.type} onChange={(e) => update(idx, "type", e.target.value)} data-testid={`template-section-type-${idx}`} className="col-span-4 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
              {sectionTypes.map((t) => <option key={t} value={t}>{sectionLabels[t] || t}</option>)}
            </select>
            <input value={s.label} onChange={(e) => update(idx, "label", e.target.value)} data-testid={`template-section-label-${idx}`} className="col-span-4 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
            <label className="col-span-2 flex items-center gap-1 text-xs font-bold text-slate-500"><input type="checkbox" checked={s.enabled} onChange={(e) => update(idx, "enabled", e.target.checked)} />Enabled</label>
            <button type="button" onClick={() => remove(idx)} data-testid={`template-section-remove-${idx}`} className="col-span-2 rounded-lg border border-slate-200 text-xs font-bold text-red-500 hover:bg-red-50 dark:border-white/15">Remove</button>
          </div>
          {s.confidence && s.confidence !== "high" && (
            <p className="mt-1.5 text-[11px] font-bold uppercase tracking-wide text-current" data-testid={`template-section-confidence-${idx}`}>
              Nova AI confidence: {s.confidence}{s.notes ? ` — ${s.notes}` : ""}
            </p>
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <button type="button" onClick={add} data-testid={`template-add-section-${template.template_id}`} className={outlineBtn}>+ Add Section</button>
        <button type="button" onClick={() => onSave(template.template_id, sections)} disabled={busy} data-testid={`template-save-sections-${template.template_id}`} className={outlineBtn}>Save Sections</button>
        {template.status !== "active" && <button type="button" onClick={() => onApprove(template.template_id)} disabled={busy || sections.length === 0} data-testid={`template-approve-${template.template_id}`} className={btn}><CheckCircle2 size={13} /> Approve & Activate</button>}
      </div>
    </div>
  );
};

export default function OrgFinanceEstimatesProposals() {
  const { API } = useAuth();
  const [settings, setSettings] = useState(null);
  const [emailReady, setEmailReady] = useState(true);
  const [meta, setMeta] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [previewTemplateId, setPreviewTemplateId] = useState(null);

  const [newName, setNewName] = useState("");
  const [newScope, setNewScope] = useState("global");
  const [newSource, setNewSource] = useState("manual_custom");
  const [uploadFile, setUploadFile] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/org/finance/settings`, { withCredentials: true }).then(({ data }) => { setSettings(data.settings); setEmailReady(data.proposal_email_ready); });
    axios.get(`${API}/org/finance/templates`, { withCredentials: true }).then(({ data }) => setTemplates(data)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API]);

  useEffect(() => { load(); }, [load]);

  if (!settings || !templates) return <div className="p-8 text-sm text-slate-400">Loading…</div>;

  const saveSettings = async () => {
    setBusy(true);
    try { await axios.patch(`${API}/org/finance/settings`, settings, { withCredentials: true }); load(); } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const createTemplate = async () => {
    if (!newName.trim()) { setError("Name is required"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/org/finance/templates`, { name: newName, source: newSource, assigned_scope: newScope }, { withCredentials: true });
      setNewName(""); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const uploadTemplate = async () => {
    if (!newName.trim() || !uploadFile) { setError("Name and file are required"); return; }
    setBusy(true);
    const form = new FormData();
    form.append("name", newName); form.append("assigned_scope", newScope); form.append("file", uploadFile);
    try {
      await axios.post(`${API}/org/finance/templates/upload`, form, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setNewName(""); setUploadFile(null); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const analyzeTemplate = async (id) => {
    setBusy(true); setError("");
    try { await axios.post(`${API}/org/finance/templates/${id}/analyze`, {}, { withCredentials: true }); load(); } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const saveSections = async (id, sections) => {
    setBusy(true);
    try { await axios.patch(`${API}/org/finance/templates/${id}`, { sections }, { withCredentials: true }); load(); } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const approveTemplate = async (id) => {
    setBusy(true);
    try { await axios.post(`${API}/org/finance/templates/${id}/approve`, {}, { withCredentials: true }); load(); } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const archiveTemplate = async (id) => {
    setBusy(true);
    try { await axios.patch(`${API}/org/finance/templates/${id}/archive`, {}, { withCredentials: true }); load(); } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6" data-testid="finance-estimates-proposals-page">
      <Link to="/dashboard/connections" className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"><ArrowLeft size={14} /> Back to Connections</Link>
      <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Estimates & Proposals</h1>
      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-600 dark:bg-red-500/10 dark:text-red-400" data-testid="finance-error">{error}</p>}
      {!emailReady && (
        <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="finance-email-not-ready-notice">
          Set a Customer-Facing Email in <Link to="/dashboard/settings" className="underline">Settings</Link> before Proposals can be emailed to customers.
        </p>
      )}

      <div className={card} data-testid="finance-settings-card">
        <h2 className="mb-3 text-sm font-extrabold text-slate-900 dark:text-white">Defaults</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Global Default Template</label>
            <select value={settings.default_template_id || "nova_standard_default"} onChange={(e) => setSettings((s) => ({ ...s, default_template_id: e.target.value }))} data-testid="finance-default-template-select" className={inputClass}>
              <option value="nova_standard_default">Nova Standard</option>
              {templates.templates.filter((t) => t.status === "active").map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>&nbsp;</label>
            <button type="button" onClick={() => setPreviewTemplateId(settings.default_template_id || "nova_standard_default")} data-testid="finance-preview-default-template-btn" className={`${outlineBtn} mt-1`}><Eye size={13} /> Preview Default as Customer</button>
          </div>
          <div>
            <label className={labelClass}>Quote Validity (days)</label>
            <input type="number" min={1} value={settings.quote_validity_days} onChange={(e) => setSettings((s) => ({ ...s, quote_validity_days: Number(e.target.value) }))} data-testid="finance-quote-validity-input" className={inputClass} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Default Payment Terms</label>
            <input value={settings.default_payment_terms || ""} onChange={(e) => setSettings((s) => ({ ...s, default_payment_terms: e.target.value }))} data-testid="finance-payment-terms-input" className={inputClass} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Default Terms & Conditions</label>
            <textarea rows={3} value={settings.default_terms_conditions || ""} onChange={(e) => setSettings((s) => ({ ...s, default_terms_conditions: e.target.value }))} data-testid="finance-terms-conditions-input" className={inputClass} />
          </div>
        </div>
        <button type="button" onClick={saveSettings} disabled={busy} data-testid="finance-save-settings-btn" className={`${btn} mt-3`}>Save Defaults</button>
      </div>

      <div className={card} data-testid="finance-templates-card">
        <h2 className="mb-3 text-sm font-extrabold text-slate-900 dark:text-white">Proposal Templates</h2>

        <div className="mb-4 grid gap-2 rounded-xl border border-dashed border-slate-300 p-3 dark:border-white/15 sm:grid-cols-4">
          <input placeholder="Template name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="finance-new-template-name" className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
          <select value={newScope} onChange={(e) => setNewScope(e.target.value)} data-testid="finance-new-template-scope" className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="global">Global</option><option value="service">Service</option><option value="product">Product</option><option value="bundle">Bundle</option>
          </select>
          <select value={newSource} onChange={(e) => setNewSource(e.target.value)} data-testid="finance-new-template-source" className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="manual_custom">Manual Custom</option><option value="nova_standard">Nova Standard base</option>
          </select>
          <button type="button" onClick={createTemplate} disabled={busy} data-testid="finance-create-template-btn" className={btn}>Create</button>
        </div>
        <div className="mb-5 grid gap-2 rounded-xl border border-dashed border-slate-300 p-3 dark:border-white/15 sm:grid-cols-4">
          <input placeholder="Existing template name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="finance-upload-template-name" className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
          <select value={newScope} onChange={(e) => setNewScope(e.target.value)} data-testid="finance-upload-template-scope" className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="global">Global</option><option value="service">Service</option><option value="product">Product</option><option value="bundle">Bundle</option>
          </select>
          <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} data-testid="finance-upload-template-file" className="text-xs" />
          <button type="button" onClick={uploadTemplate} disabled={busy} data-testid="finance-upload-template-btn" className={btn}><Upload size={13} /> Upload Existing</button>
        </div>

        <div className="space-y-3" data-testid="finance-templates-list">
          {templates.templates.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No templates yet — create one or upload an existing estimate/proposal.</p>
          ) : templates.templates.map((t) => (
            <div key={t.template_id} className="rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid={`finance-template-${t.template_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">{t.name}</p>
                  <p className="text-xs text-slate-400">{SOURCE_LABELS[t.source]} · {t.assigned_scope} · <span className="font-bold">{STATUS_LABELS[t.status]}</span></p>
                </div>
                <div className="flex gap-2">
                  {(t.sections?.length > 0) && (
                    <button type="button" onClick={() => setPreviewTemplateId(t.template_id)} data-testid={`finance-preview-template-${t.template_id}`} className={outlineBtn}><Eye size={13} /> Preview as Customer</button>
                  )}
                  {t.source === "org_uploaded" && t.ai_analysis?.status !== "analyzing" && (t.ai_analysis?.status === "not_started" || t.ai_analysis?.status === "failed") && (
                    <button type="button" onClick={() => analyzeTemplate(t.template_id)} disabled={busy} data-testid={`finance-analyze-template-${t.template_id}`} className={outlineBtn}><Sparkles size={13} /> Analyze with Nova AI</button>
                  )}
                  {t.status === "active" && <button type="button" onClick={() => archiveTemplate(t.template_id)} disabled={busy} data-testid={`finance-archive-template-${t.template_id}`} className={outlineBtn}>Archive</button>}
                  <button type="button" onClick={() => setExpandedId(expandedId === t.template_id ? null : t.template_id)} data-testid={`finance-expand-template-${t.template_id}`} className={outlineBtn}>{expandedId === t.template_id ? "Collapse" : "Edit Sections"}</button>
                </div>
              </div>
              {t.ai_analysis?.status === "analyzing" && <p className="mt-2 text-xs font-bold text-blue-600 dark:text-blue-400" data-testid={`finance-ai-analyzing-${t.template_id}`}>Analyzing with Nova AI…</p>}
              {t.ai_analysis?.status === "needs_review" && <p className="mt-2 text-xs font-bold text-amber-600 dark:text-amber-400" data-testid={`finance-ai-needs-review-${t.template_id}`}>Nova AI draft ready — review sections below before approving.</p>}
              {t.ai_analysis?.status === "failed" && <p className="mt-2 text-xs font-bold text-red-600 dark:text-red-400" data-testid={`finance-ai-failed-${t.template_id}`}>Analysis failed: {t.ai_analysis.error}. Your uploaded file is unchanged.</p>}
              {expandedId === t.template_id && (
                <TemplateSectionsEditor template={t} sectionTypes={templates.section_types} sectionLabels={templates.section_labels} onSave={saveSections} onApprove={approveTemplate} busy={busy} />
              )}
            </div>
          ))}
        </div>
      </div>
      {previewTemplateId && <TemplatePreviewModal templateId={previewTemplateId} onClose={() => setPreviewTemplateId(null)} API={API} />}
    </div>
  );
}
